import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface PresetSectionProps {
  title: ReactNode;
  icon?: ReactNode;
  rightSlot?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function PresetSection({
  title,
  icon,
  rightSlot,
  defaultOpen = true,
  children,
}: PresetSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-white/[0.03] transition-colors"
      >
        {icon}
        <span className="text-xs font-semibold text-white/85">{title}</span>
        <span className="ml-auto flex items-center gap-2">
          {rightSlot}
          {open ? (
            <ChevronUp size={14} className="text-white/40" />
          ) : (
            <ChevronDown size={14} className="text-white/40" />
          )}
        </span>
      </button>
      {open && <div className="px-4 py-3 border-t border-white/[0.06] space-y-3">{children}</div>}
    </div>
  );
}

export function PresetToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 ${disabled ? "opacity-50" : ""}`}>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative shrink-0 w-9 h-5 rounded-full transition-colors ${checked ? "bg-blue-500" : "bg-white/[0.12]"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : ""}`}
        />
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-white/85">{label}</div>
        {description && (
          <div className="text-[11px] text-white/40 mt-0.5 leading-snug">{description}</div>
        )}
      </div>
    </div>
  );
}

export function PresetSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: ReactNode;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-white/45 font-medium">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white/90 focus:outline-none focus:border-blue-500/50"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-[#11151d]">
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PresetTextInput({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  rightHint,
}: {
  label: ReactNode;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  rightHint?: ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-white/45 font-medium">
          {label}
        </span>
        {rightHint && <span className="text-[10px] text-white/30">{rightHint}</span>}
      </div>
      <input
        type="text"
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white/90 placeholder:text-white/25 focus:outline-none focus:border-blue-500/50"
      />
    </label>
  );
}
